/**
 * Pure helpers for the inbound-reply review UI and the workflow gates
 * (TRUS-757 F4/F5). No React, no network — unit-tested directly.
 */
import type { ReferralInboundItem } from '@/services/api/referralInbound';

// ---------------------------------------------------------------------------
// Member display: first name + last initial, never more.
// ---------------------------------------------------------------------------

export function memberShortName(
  item: { member_first_name?: string | null; member_last_initial?: string | null; member_name?: string | null },
): string | null {
  const first = (item.member_first_name || '').trim();
  const initial = (item.member_last_initial || '').trim().charAt(0);
  if (first) return initial ? `${first} ${initial.toUpperCase()}.` : first;
  const full = (item.member_name || '').trim();
  if (!full) return null;
  const parts = full.split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
}

// ---------------------------------------------------------------------------
// Confidence
// ---------------------------------------------------------------------------

export type ConfidenceTone = 'high' | 'medium' | 'low';

export function confidenceTone(c: number): ConfidenceTone {
  if (c >= 0.85) return 'high';
  if (c >= 0.6) return 'medium';
  return 'low';
}

export const CONFIDENCE_CLASS: Record<ConfidenceTone, string> = {
  high: 'bg-success-soft text-success-ink',
  medium: 'bg-attention-soft text-attention-ink',
  low: 'bg-danger-soft text-danger-ink',
};

export function formatConfidence(c: number): string {
  return `${Math.round(c * 100)}%`;
}

// ---------------------------------------------------------------------------
// Which actions a row offers
// ---------------------------------------------------------------------------

const FINAL: ReadonlySet<string> = new Set(['applied', 'rejected']);

export interface InboundActions {
  apply: boolean;
  editApply: boolean;
  reject: boolean;
  link: boolean;
}

export function availableActions(item: Pick<ReferralInboundItem, 'status' | 'service_referral_id' | 'proposed_transition'>): InboundActions {
  const open = !FINAL.has(item.status);
  const linked = item.service_referral_id != null;
  return {
    apply: open && linked && !!item.proposed_transition && item.status !== 'unmatched',
    editApply: open && linked,
    reject: open,
    link: open && (!linked || item.status === 'unmatched'),
  };
}

// ---------------------------------------------------------------------------
// Referral status order (contract: VALID_STATUS_TRANSITIONS)
// ---------------------------------------------------------------------------

const STATUS_RANK: Record<string, number> = {
  created: 0,
  pending_consent: 0,
  matching: 0,
  matched: 1,
  provider_notified: 2,
  provider_accepted: 3,
  provider_declined: 3,
  service_scheduled: 4,
  service_started: 5,
  service_completed: 6,
  billing_ready: 6,
  billed: 6,
  paid: 6,
  closed: 7,
};

export function statusRank(status: string | null | undefined): number {
  if (!status) return -1;
  return STATUS_RANK[status] ?? -1;
}

/**
 * Has a referral in `current` reached the gate's `status_at_least`?
 * A declined referral never satisfies an "accepted" (or later) gate, and an
 * accepted one never satisfies a "declined" gate.
 */
export function referralReached(current: string | null | undefined, atLeast: string): boolean {
  if (!current) return false;
  if (current === atLeast) return true;
  if (current === 'provider_declined' && statusRank(atLeast) >= 3) return false;
  if (atLeast === 'provider_declined') return false;
  return statusRank(current) >= statusRank(atLeast) && statusRank(atLeast) >= 0;
}

// ---------------------------------------------------------------------------
// Workflow gate steps
// ---------------------------------------------------------------------------

export interface GateConfig {
  type?: string;
  status_at_least?: string;
  timeout_hours?: number;
  on_timeout?: 'remind' | 'escalate' | string;
}

export interface StepLike {
  step_number: number;
  step_name?: string | null;
  step_type: string;
  status: string;
  scheduled_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at?: string | null;
  step_input?: Record<string, unknown> | null;
}

export type GateState = 'done' | 'waiting' | 'overdue' | 'upcoming' | 'skipped' | 'failed';

export interface EvaluatedStep {
  step: StepLike;
  gate: GateConfig | null;
  state: GateState;
  /** When the timeout runs out (gate steps with timeout_hours only). */
  dueAt: Date | null;
}

export function gateConfigOf(step: StepLike): GateConfig | null {
  const cfg = (step.step_input || {}) as GateConfig;
  if (step.step_type === 'referral_status_gate' || cfg.type === 'referral_status_gate') {
    const hours = Number(cfg.timeout_hours);
    return {
      type: 'referral_status_gate',
      status_at_least: typeof cfg.status_at_least === 'string' ? cfg.status_at_least : undefined,
      timeout_hours: Number.isFinite(hours) && hours > 0 ? hours : undefined,
      on_timeout: cfg.on_timeout,
    };
  }
  return null;
}

const ACTIVE_STEP_STATUSES = new Set(['executing', 'waiting', 'in_progress', 'running']);

/**
 * Evaluate every step: done / waiting / overdue / upcoming.
 * The active step is the one the engine marks executing/waiting, or else the
 * first step that is not done while the instance is still running.
 */
export function evaluateSteps(
  steps: StepLike[],
  opts: { referralStatus?: string | null; instanceStatus?: string | null; now?: Date } = {},
): EvaluatedStep[] {
  const now = opts.now ?? new Date();
  const ordered = [...steps].sort((a, b) => a.step_number - b.step_number);
  const instanceOpen = !['completed', 'cancelled', 'failed'].includes((opts.instanceStatus || '').toLowerCase());

  const base = ordered.map((step) => {
    const gate = gateConfigOf(step);
    let done = step.status === 'completed';
    if (!done && gate?.status_at_least && referralReached(opts.referralStatus, gate.status_at_least)) done = true;
    return { step, gate, done };
  });

  const explicitActive = base.some((b) => !b.done && ACTIVE_STEP_STATUSES.has(b.step.status));
  let implicitActiveIdx = -1;
  if (!explicitActive && instanceOpen) {
    implicitActiveIdx = base.findIndex((b) => !b.done && b.step.status !== 'skipped' && b.step.status !== 'failed');
  }

  let previousCompletedAt: string | null = null;
  return base.map((b, idx) => {
    const { step, gate, done } = b;
    let state: GateState;
    let dueAt: Date | null = null;
    if (done) state = 'done';
    else if (step.status === 'skipped') state = 'skipped';
    else if (step.status === 'failed') state = 'failed';
    else if (ACTIVE_STEP_STATUSES.has(step.status) || idx === implicitActiveIdx) state = 'waiting';
    else state = 'upcoming';

    if (gate?.timeout_hours) {
      const startRef = step.started_at || step.scheduled_at || previousCompletedAt || null;
      if (startRef) {
        const start = new Date(startRef);
        if (!Number.isNaN(start.getTime())) {
          dueAt = new Date(start.getTime() + gate.timeout_hours * 3600_000);
          if (state === 'waiting' && dueAt.getTime() < now.getTime()) state = 'overdue';
        }
      }
    }
    if (step.completed_at) previousCompletedAt = step.completed_at;
    return { step, gate, state, dueAt };
  });
}

/** "72h" / "7d" for a timeout in hours. */
export function formatTimeout(hours: number): string {
  if (hours >= 24 && hours % 24 === 0) return `${hours / 24}d`;
  return `${hours}h`;
}
